import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import { Auth0ContextInterface } from '@auth0/auth0-react/src/auth0-context';
import { useEffect, useMemo, useState } from 'react';

import { useGetIdTokenClaims } from '@/hooks/queries/auth';
import { ReportType } from '@/lib/api/schemas/reports';
import {
  Action,
  PermissionsService,
  Role,
  Subject,
} from '@/lib/auth/permissions/permissions-service';

export type UseAuthOut = {
  accessToken: string | null;
  loginWithRedirect: Auth0ContextInterface['loginWithRedirect'];
  signOut: Auth0ContextInterface['logout'];
  user: Auth0ContextInterface['user'];
  hasPermission: (subject: Subject, action: Action) => boolean;
  hasAccessToReport: (reportType: ReportType) => boolean;
  hasAccessToLocation: (locationId: string) => boolean;
  hasRole: (role: string | Role) => boolean;
  hasAccessToSubLocation: (
    locationID: string,
    subLocationID: string
  ) => boolean;
  hasAccessToAllSublocationsWithinLocation: (locationId: string) => boolean;
  /** Sole concrete sub-lot id when user is restricted to one sub on that lot; else null. */
  getSoleValidatorSubLocationId: (locationId: string) => string | null;
  /** Whole main-lot scope (root or all sub-lots) for `locationId`, for group permit disclosure. */
  hasFullMainLotScope: (locationId: string) => boolean;
  isSuperAdmin: () => boolean;
  /** Location boundary covers every location; callers still check the permission. */
  hasAllLocations: () => boolean;
  isGovOfficer: () => boolean;
  isAuthenticated: Auth0ContextInterface['isAuthenticated'];
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'];
  /**
   * @deprecated Use roles and permissions instead.
   */
  allowedToEditAndCreate: boolean;
  orgMetadata: Record<string, string> | null;

  isLoading: boolean;
  roles: Role[] | null;
  orgName: string | null;
  orgId: string | null;
  userId: string | null;
  permissions: `${Action}:${Subject}`[] | null;
  allowedLocationIds: string[] | 'all';
  hasAccessToPortal: boolean;
};

export function useAuth() {
  const {
    user,
    isAuthenticated,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
    isLoading: isLoadingAuth0,
  } = useAuth0();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [internalIsLoading, setInternalIsLoading] = useState(
    // NOTE: internalIsLoading has to be true if user is authenticated from the start
    // because it affects the code that relies on this hook. 9 times out of 10
    // when component renders permissions or any other field related to access token/token claims
    // will be empty and so ProtectedRoute or any other component will reject the client by redirecting
    // the user to initial page or automatically sign the user out. This is not intended because
    // we don't know anything about user's permissions or token claims content yet since
    // we didn't load it yet. It's crucial to keep this true on initial render to avoid
    // these types of issues(race conditions).
    isAuthenticated
  );
  const { data: idTokenClaims, isLoading: isIdTokenClaimsLoading } =
    useGetIdTokenClaims();

  const permissionsService = useMemo(() => {
    if (!idTokenClaims || !accessToken) return null;

    return new PermissionsService(idTokenClaims, accessToken);
  }, [idTokenClaims, accessToken]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void (async () => {
      setInternalIsLoading(true);
      try {
        const accessToken = await getAccessTokenSilently();
        setAccessToken(accessToken);
      } catch (error) {
        console.error('Error getting access token or id token claims: ', error);
      } finally {
        setInternalIsLoading(false);
      }
    })();
  }, [isAuthenticated, getAccessTokenSilently]);

  const isLoading =
    isLoadingAuth0 || internalIsLoading || isIdTokenClaimsLoading;

  const hasAccessToPortal =
    isLoading || !isAuthenticated || !permissionsService
      ? true
      : permissionsService.hasAccessToPortal();

  return {
    accessToken: permissionsService?.getAccessToken() ?? null,
    signOut: (options?: LogoutOptions) => {
      return logout({
        ...options,
        logoutParams: { returnTo: window.location.origin },
      });
    },
    hasAccessToSubLocation: (locationID: string, subLocationID: string) =>
      permissionsService?.hasAccessToSublocation(locationID, subLocationID) ??
      false,
    hasAccessToAllSublocationsWithinLocation: (locationId: string) =>
      permissionsService?.hasAccessToAllSublocationsWithinLocation(
        locationId
      ) ?? false,
    getSoleValidatorSubLocationId: (locationId: string) =>
      permissionsService?.getSoleValidatorSubLocationId(locationId) ?? null,
    hasFullMainLotScope: (locationId: string) =>
      permissionsService?.hasFullMainLotScope(locationId) ?? false,
    user,
    permissions: permissionsService?.getPermissions() ?? null,
    roles: permissionsService?.getRoles() ?? null,
    hasPermission: (subject: Subject, action: Action) =>
      permissionsService?.hasPermission(subject, action) ?? false,
    hasAccessToReport: (reportType: ReportType) =>
      permissionsService?.hasAccessToReport(reportType) ?? false,
    hasRole: (role: string | Role) =>
      permissionsService?.hasRole(role) ?? false,
    hasAccessToLocation: (locationId: string) =>
      permissionsService?.hasAccessToLocation(locationId) ?? false,
    isSuperAdmin: () => permissionsService?.isSuperAdmin() ?? false,
    hasAllLocations: () => permissionsService?.hasAllLocations() ?? false,
    isGovOfficer: () => permissionsService?.isGovOfficer() ?? false,
    allowedLocationIds: permissionsService?.getAllowedLocationIds() ?? [],
    isAuthenticated,
    loginWithRedirect,
    userId: permissionsService?.getUserId() ?? null,
    getAccessTokenSilently,
    orgName: permissionsService?.getOrgName(),
    orgId: permissionsService?.getOrgId() ?? null,
    orgMetadata: permissionsService?.getOrgMetadata(),
    isLoading,
    allowedToEditAndCreate:
      permissionsService?.hasRole(Role.SuperAdmin) ?? false,
    hasAccessToPortal: hasAccessToPortal,
  } as UseAuthOut;
}

export {
  type Action,
  Role,
  Subject,
} from '@/lib/auth/permissions/permissions-service';
